/**
 * Live subscription + writer for the user's pinned-favorites currency
 * list. Stored on the user profile so pins sync across devices — a user
 * who pins EUR/RON on the web sees them at the top of the picker on
 * mobile too.
 *
 * Used by CurrencyPicker: read shape → `pinnedCurrencies`, write shape
 * → `setPinnedCurrencies(next)`. The write is fire-and-forget from the
 * caller's perspective — the snapshot will pick up the change on the
 * next tick and re-render the picker with the new pin order.
 */

import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

export function usePinnedCurrencies(): {
  pinnedCurrencies: string[];
  setPinnedCurrencies: (next: string[]) => Promise<void>;
} {
  const { uid } = useAuth();
  const [pinnedCurrencies, setLocal] = useState<string[]>([]);

  useEffect(() => {
    if (!uid) {
      setLocal([]);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data();
        const p = Array.isArray(data?.pinnedCurrencies)
          ? (data!.pinnedCurrencies as unknown[]).filter(
              (x): x is string => typeof x === 'string',
            )
          : [];
        setLocal(p);
      },
      (err) => {
        console.warn(
          '[usePinnedCurrencies] profile read failed:',
          err?.message ?? err,
        );
        setLocal([]);
      },
    );
    return unsub;
  }, [uid]);

  const setPinnedCurrencies = useCallback(
    async (next: string[]) => {
      if (!uid) return;
      try {
        await updateDoc(doc(db, 'users', uid), { pinnedCurrencies: next });
      } catch (err) {
        console.warn(
          '[usePinnedCurrencies] profile write failed:',
          (err as Error)?.message ?? err,
        );
      }
    },
    [uid],
  );

  return { pinnedCurrencies, setPinnedCurrencies };
}
